<aside class="sidepanel">
  <header class="sidepanel__head">
    <app-icon name="table" size="18" />
    <span class="sidepanel__title">{{ S['Conditional formatting'] }}</span>
    <button class="iconbtn iconbtn--bare" [title]="S['Close']" (click)="cancelled.emit()" [attr.aria-label]="S['Close']">
      <app-icon name="close" size="16" />
    </button>
  </header>

  <div class="sidepanel__body">
    <div class="rules__head">
      <span class="field__label">{{ S['Rules on this sheet'] }}</span>
      <span class="rules__hint">{{ S['Top rule wins'] }}</span>
    </div>

    @for (r of rules(); track r.id) {
      <button
        class="rulerow"
        [class.rulerow--on]="r.id === selectedId()"
        (click)="selectRule(r)"
        [attr.aria-label]="r.name + ' ' + r.range"
        aria-keyshortcuts="Delete"
        (keydown.delete)="deleteRule(r.id, $event)"
      >
        <span class="option__grip"><app-icon name="dots" size="14" /></span>
        <span class="rulerow__badge" [class.rulerow__badge--bar]="r.style === 'databar'">
          @if (r.style === 'databar') {
            <span class="rulerow__bar"></span>
          } @else {
            {{ S['Aa'] }}
          }
        </span>
        <span class="rulerow__text">
          <span class="rulerow__name">{{ r.name }}</span>
          <span class="rulerow__range">{{ r.range }}</span>
        </span>
        <span class="iconbtn iconbtn--bare" [title]="S['Delete rule']" (click)="deleteRule(r.id, $event)">
          <app-icon name="trash" size="14" />
        </span>
      </button>
    } @empty {
      <p class="rules__empty">{{ S['No rules on this sheet yet.'] }}</p>
    }
    <button class="linkbtn" (click)="addRule()" [attr.aria-label]="S['Add rule']"><app-icon name="plus" size="14" /> {{ S['Add rule'] }}</button>

    <div class="field__label field__label--collapsible cf__edithead">
      <app-icon name="chevrondown" size="13" /> {{ selectedId() ? S['Edit rule'] : S['New rule'] }}
    </div>

    <label class="field">
      <span class="field__label">{{ S['Apply to range'] }}</span>
      <span class="field__row">
        <input class="input" [value]="range()" (input)="setRange($event)" [attr.aria-label]="S['Apply to range']"/>
        <button class="iconbtn" [title]="S['Pick range']" [attr.aria-label]="S['Pick range']"><app-icon name="grid" size="15" /></button>
      </span>
    </label>

    <div class="field">
      <span class="field__label">{{ S['Format style'] }}</span>
      <span class="segmented">
        @for (s of styles; track s.key) {
          <button class="segmented__btn" [class.segmented__btn--on]="style() === s.key" (click)="style.set(s.key)" [attr.aria-label]="s.label">
            {{ s.label }}
          </button>
        }
      </span>
    </div>

    @switch (style()) {
      @case ('databar') {
        <div class="field__pair">
          <label class="field">
            <span class="field__label">{{ S['Shortest bar'] }}</span>
            <select class="input" (change)="shortest.set(pick($event))" [attr.aria-label]="S['Shortest bar']">
              <option value="Lowest" [selected]="shortest() === 'Lowest'">{{ S['Lowest'] }}</option>
              <option value="Number" [selected]="shortest() === 'Number'">{{ S['Number'] }}</option>
              <option value="Percent" [selected]="shortest() === 'Percent'">{{ S['Percent'] }}</option>
            </select>
          </label>
          <label class="field">
            <span class="field__label">{{ S['Longest bar'] }}</span>
            <select class="input" (change)="longest.set(pick($event))" [attr.aria-label]="S['Longest bar']">
              <option value="Highest" [selected]="longest() === 'Highest'">{{ S['Highest'] }}</option>
              <option value="Number" [selected]="longest() === 'Number'">{{ S['Number'] }}</option>
              <option value="Percent" [selected]="longest() === 'Percent'">{{ S['Percent'] }}</option>
            </select>
          </label>
        </div>
        <div class="field">
          <span class="field__label">{{ S['Bar colour'] }}</span>
          <span class="field__pair">
            <button class="swatchbtn" (click)="cycleBar()" [attr.aria-label]="S['Bar colour']">
              <span class="swatchbtn__dot" [style.background]="barColour()"></span>{{ barColour() }}
            </button>
            <button class="swatchbtn" (click)="cycleNegative()" [attr.aria-label]="S['Negative']">
              <span class="swatchbtn__dot" [style.background]="negativeColour()"></span>{{ S['Negative'] }}
            </button>
          </span>
        </div>
        <label class="checkrow">
          <span>{{ S['Gradient fill'] }}</span>
          <input type="checkbox" [checked]="gradient()" (change)="gradient.set(!gradient())" [attr.aria-label]="S['Gradient fill']" />
        </label>
        <label class="checkrow">
          <span>{{ S['Show bar only'] }}</span>
          <input type="checkbox" [checked]="barOnly()" (change)="barOnly.set(!barOnly())" [attr.aria-label]="S['Show bar only']" />
        </label>
      }
      @case ('highlight') {
        <label class="field">
          <span class="field__label">{{ S['When the cell'] }}</span>
          <select class="input" (change)="setCondKind($event)" [attr.aria-label]="S['When the cell']">
            @for (k of condKinds; track k.key) {
              <option [value]="k.key" [selected]="condKind() === k.key">{{ k.label }}</option>
            }
          </select>
        </label>

        @if (operatorsFor().length) {
          <label class="field">
            <span class="field__label">{{ S['Condition'] }}</span>
            <select class="input" (change)="setOperator($event)" [attr.aria-label]="S['Condition']">
              @for (o of operatorsFor(); track o.key) {
                <option [value]="o.key" [selected]="operator() === o.key">{{ o.label }}</option>
              }
            </select>
          </label>
        }

        @if (condKind() === 'date') {
          <label class="field">
            <span class="field__label">{{ S['Period'] }}</span>
            <select class="input" (change)="setTimePeriod($event)" [attr.aria-label]="S['Period']">
              @for (t of timePeriods; track t.key) {
                <option [value]="t.key" [selected]="timePeriod() === t.key">{{ t.label }}</option>
              }
            </select>
          </label>
        }

        @if (condKind() === 'formula') {
          <label class="field">
            <span class="field__label">{{ S['Formula'] }}</span>
            <input class="input" [value]="formula()" (input)="setFormula($event)" [attr.aria-label]="S['Formula']"/>
          </label>
        }

        @if (condKind() === 'rank') {
          <div class="field__pair">
            <label class="field">
              <span class="field__label">{{ S['From the'] }}</span>
              <select class="input" (change)="rankBottom.set($any($event.target).value === 'bottom')" [attr.aria-label]="S['From the']">
                <option value="top" [selected]="!rankBottom()">{{ S['Top'] }}</option>
                <option value="bottom" [selected]="rankBottom()">{{ S['Bottom'] }}</option>
              </select>
            </label>
            <label class="field">
              <span class="field__label">{{ S['Measured in'] }}</span>
              <select class="input" (change)="rankPercent.set($any($event.target).value === 'percent')" [attr.aria-label]="S['Measured in']">
                <option value="items" [selected]="!rankPercent()">{{ S['Items'] }}</option>
                <option value="percent" [selected]="rankPercent()">{{ S['Percent'] }}</option>
              </select>
            </label>
          </div>
          <label class="field">
            <span class="field__label">{{ S['How many'] }}</span>
            <input class="input" type="number" [value]="rankValue()" (input)="setRankValue($event)" [attr.aria-label]="S['How many']"/>
          </label>
        }

        @if (takesValue()) {
          <div class="field">
            <span class="field__label">{{ takesTwoValues() ? S['Between'] : S['Value'] }}</span>
            <span class="field__row">
              <input class="input" [value]="value()" (input)="setValue($event)" [attr.aria-label]="takesTwoValues() ? S['Between'] : S['Value']" />
              @if (takesTwoValues()) {
                <input class="input" [value]="value2()" (input)="setValue2($event)" [attr.aria-label]="S['Between']" />
              }
            </span>
          </div>
        }
        <div class="field">
          <span class="field__label">{{ S['Format'] }}</span>
          <span class="field__pair">
            <button class="swatchbtn" (click)="cycleFill()" [attr.aria-label]="S['Fill']">
              <span class="swatchbtn__dot" [style.background]="fill()"></span>{{ S['Fill'] }}
            </button>
            <button class="swatchbtn" (click)="cycleText()" [attr.aria-label]="S['Text']">
              <span class="swatchbtn__dot" [style.background]="textColour()"></span>{{ S['Text'] }}
            </button>
          </span>
        </div>
      }
      @case ('scale') {
        <label class="checkrow">
          <span>{{ S['Use a midpoint'] }}</span>
          <input type="checkbox" [checked]="scaleThreePoint()" (change)="scaleThreePoint.set(!scaleThreePoint())" [attr.aria-label]="S['Use a midpoint']" />
        </label>
        <div class="field">
          <span class="field__label">{{ S['Colours'] }}</span>
          <span class="field__pair">
            <button class="swatchbtn" (click)="cycleScale('min')" [attr.aria-label]="S['Min']">
              <span class="swatchbtn__dot" [style.background]="scaleMin()"></span>{{ S['Min'] }}
            </button>
            @if (scaleThreePoint()) {
              <button class="swatchbtn" (click)="cycleScale('mid')" [attr.aria-label]="S['Mid']">
                <span class="swatchbtn__dot" [style.background]="scaleMid()"></span>{{ S['Mid'] }}
              </button>
            }
            <button class="swatchbtn" (click)="cycleScale('max')" [attr.aria-label]="S['Max']">
              <span class="swatchbtn__dot" [style.background]="scaleMax()"></span>{{ S['Max'] }}
            </button>
          </span>
        </div>
        <div class="scale__preview" [style.background]="scalePreview()"></div>
      }
      @case ('icons') {
        <label class="field">
          <span class="field__label">{{ S['Icon set'] }}</span>
          <select class="input" (change)="iconGroup.set(pick($event))" [attr.aria-label]="S['Icon set']">
            @for (g of iconGroups; track g) {
              <option [value]="g" [selected]="iconGroup() === g">{{ g }}</option>
            }
          </select>
        </label>
        <label class="checkrow">
          <span>{{ S['Show the value as well'] }}</span>
          <input type="checkbox" [checked]="iconShowValue()" (change)="iconShowValue.set(!iconShowValue())" [attr.aria-label]="S['Show the value as well']" />
        </label>
        <p class="cf__todo">
          {{ S['Thresholds are the top and bottom thirds, the usual split for a three-step set.'] }}
        </p>
      }
    }
  </div>

  <footer class="sidepanel__foot">
    <span class="sidepanel__spacer"></span>
    <button class="btn btn--ghost" (click)="cancelled.emit()" [attr.aria-label]="S['Cancel']">{{ S['Cancel'] }}</button>
    <button class="btn btn--primary" (click)="emitSave()" [attr.aria-label]="S['Save rule']">{{ S['Save rule'] }}</button>
  </footer>
</aside>
